import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { HomeWidget } from "./HomeWidget";

/**
 * Compact metric card + Show more dialog + gear → Customize (plan/50 C).
 */
export function HomeMetricCard(props: {
  title: string;
  description?: string;
  size?: "list" | "wide";
  className?: string;
  /** Compact body (always visible on the card). */
  compact: ReactNode;
  /** Full body inside Show more dialog. */
  expanded: ReactNode;
  /** Optional controls above expanded body (e.g. Custom range). */
  expandedExtra?: ReactNode;
  expandedDescription?: string;
  showMoreLabel?: string;
  /** Opens page Customize (same prefs). */
  onCustomize?: () => void;
  /** Extra header actions (e.g. deep-link). */
  actions?: ReactNode;
  empty?: boolean;
  emptyMessage?: string;
  /** Controlled Show more open (Custom period). */
  showMoreOpen?: boolean;
  onShowMoreOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const controlled = props.onShowMoreOpenChange != null;
  const open = controlled ? Boolean(props.showMoreOpen) : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (controlled) props.onShowMoreOpenChange?.(next);
    else setUncontrolledOpen(next);
  };

  return (
    <>
      <HomeWidget
        title={props.title}
        description={props.description}
        size={props.size ?? "list"}
        className={cn(props.className)}
        onCustomize={props.onCustomize}
        actions={props.actions}
      >
        {props.empty ? (
          <p className="m-0 text-sm text-[var(--color-muted)]">
            {props.emptyMessage ?? "Nothing to show yet."}
          </p>
        ) : (
          props.compact
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-3 h-auto px-0 text-xs font-medium text-[var(--color-primary)]"
          onClick={() => setOpen(true)}
        >
          {props.showMoreLabel ?? "Show more"}
        </Button>
      </HomeWidget>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[min(100%-1.5rem,28rem)]">
          <DialogHeader>
            <DialogTitle>{props.title}</DialogTitle>
            {props.expandedDescription || props.description ? (
              <DialogDescription>
                {props.expandedDescription ?? props.description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          <div className="mt-3">
            {props.expandedExtra}
            {props.expanded}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
